import { linkTypes, type LinkType } from '@ev-tech/ui-core-components/utils';
import { type StoryBlokLinkProps, type StoryblokDatasourceEntry } from '@ev-tech/ui-core-components/cms';

import { PROD_ENV_URL } from '../constants';

export const getLinkUrl = (linkObject: StoryBlokLinkProps) => {
  if (linkObject.story?.url) {
    return `${linkObject.story.url}`;
  }
  if (linkObject.cached_url) {
    return `${linkObject.cached_url}`;
  }
  if (linkObject.url) {
    return `${linkObject.url}`;
  }
  return '';
};

const resolveExternalLink = ({
  externalLinkDomains,
  linkObject,
}: {
  externalLinkDomains: StoryblokDatasourceEntry[];
  linkObject: StoryBlokLinkProps;
}) => {
  const linkIsInDatasource = externalLinkDomains.find((obj) => {
    const objName = obj.name.endsWith('/') ? obj.name.slice(0, -1) : obj.name;

    return getLinkUrl(linkObject).includes(objName);
  });

  const linkType = (() => {
    if (linkIsInDatasource) {
      return linkIsInDatasource.value;
    }
    if (linkObject.linktype === 'url') {
      return linkTypes.external;
    }
    return linkTypes.internal;
  })() as LinkType;

  const isExternal = linkType === 'external';

  const rel = (() => {
    if (isExternal && linkIsInDatasource) {
      return 'noopener';
    }
    if (isExternal) {
      return 'noopener nofollow noreferrer';
    }
    return undefined;
  })();

  const target = isExternal ? '_blank' : undefined;
  return { linkType, rel, target };
};

type ResolvedLink = {
  label: string;
  rel: string | undefined;
  target: string | undefined;
  linkType: LinkType;
  href: string;
  as?: 'a';
};

// all links are considered as external for search app until we have a good solution to differentiate
// between external (use simple <a> tag) and internal (use NextJS Link) links based on consuming app
export const linkResolver = ({
  linkObject,
  label,
  externalLinkDomains,
}: {
  linkObject: StoryBlokLinkProps;
  label: string;
  externalLinkDomains: StoryblokDatasourceEntry[];
}): ResolvedLink => {
  const { rel, target } = resolveExternalLink({ externalLinkDomains, linkObject });

  const href = getLinkUrl(linkObject);

  const SEARCH_DOMAIN = process.env.NEXT_PUBLIC_SEARCH_WEBSITE || PROD_ENV_URL;

  // we must explicitly define links to search applications as external
  if (href.toLowerCase().startsWith(SEARCH_DOMAIN)) {
    return {
      label,
      href,
      linkType: linkTypes.external,
      rel: undefined,
      target: undefined,
      as: 'a',
    };
  }

  return {
    label,
    href,
    linkType: linkTypes.external,
    rel,
    target,
    as: 'a',
  };
};
